import { makeObservable, observable, action, computed, runInAction, makeAutoObservable } from "mobx"
import { BasicDirectorUserInfoDTO, DirectorUser, UserBaseModel, UserModel } from '../../API/Generated/data-contracts'
import { Users } from '../../API/Generated/Users'
import { makePersistable } from "mobx-persist-store";
import { Account } from '../../API/Generated/Account';
import { RequestStatus } from "SessionState/RequestHandling/RequestStatus";
import { DirectorUsers } from "API/Generated/DirectorUsers";

interface Request {
    id: number;
    status: RequestStatus;
}

export class UserStore {

    // Services
    UserService: Users = new Users();
    AccountService: Account = new Account();
    DirectorService: DirectorUsers = new DirectorUsers();

    // Properties
    @observable CurrentUser: UserModel;
    @observable CurrentUserBasic: UserBaseModel;
    @observable SelectedUser: UserModel | undefined;
    @observable UserList: UserModel[];
    @observable AllDirectorUsersList: UserModel[];
    @observable AllCoachUsersList: UserModel[];

    isHydrated: boolean = false;


    CurrentUserRequest: Request = {
        id: 0,
        status: RequestStatus.PENDING
    }
    GetUserByIdRequest?: Request = {
        id: 0,
        status: RequestStatus.PENDING
    }
    AllActiveDirectorsListRequest?: Request = {
        id: 0,
        status: RequestStatus.PENDING
    }
    

// Constructors
constructor(
    currentUser: UserModel,
    currentUserBasic: UserBaseModel,
    userList: UserModel[],
    allDirectorUsersList: UserModel[],
    allCoachUsersList: UserModel[],
    currentUserRequest: Request,
    selectedUser?: UserModel,
    getUserByIdRequest?: Request
) {
    // Set initial values first
    this.CurrentUser = currentUser;
    this.CurrentUserBasic = currentUserBasic;
    this.UserList = userList;
    this.AllDirectorUsersList = allDirectorUsersList;
    this.AllCoachUsersList = allCoachUsersList;
    this.SelectedUser = selectedUser;
    this.CurrentUserRequest = currentUserRequest;
    this.GetUserByIdRequest = getUserByIdRequest;

    makeAutoObservable(this);

    void this.initializePersistence();
}

private async initializePersistence(): Promise<void> {
    try {
        await makePersistable(this, {
            name: "UserStore",
            properties: [
                "CurrentUser",
                "CurrentUserBasic",
            ],
            storage: window.localStorage,
        });

        console.log("🔐 USER STORE HYDRATED", {
            currentUserId: this.CurrentUser?.id,
            currentUserRoles: this.CurrentUser?.roles,
            basicUserId: this.CurrentUserBasic?.id,
            basicRoles: this.CurrentUserBasic?.roles,
        });
    } catch (error) {
        console.error(
            "❌ Failed to hydrate UserStore:",
            error
        );
    } finally {
        runInAction(() => {
            this.isHydrated = true;
        });
    }
}

    // Actions
    @action
    async setLoggedInUserOrg() {
        try {
            var currentUser = await this.AccountService.currentUserList()
            // if (this.CurrentUserRequest.id != currentUser.data ||
            //     (this.CurrentUserRequest.id == currentUser.data && (this.CurrentUserRequest.status == RequestStatus.PENDING || this.CurrentUserRequest.status == RequestStatus.ERROR || this.CurrentUserRequest.status == RequestStatus.COMPLETED))) {
                this.CurrentUserRequest.id = currentUser.data;
                this.CurrentUserRequest.status = RequestStatus.ACTIVE
                var userResult = await this.UserService.getByUserId(currentUser.data);
                runInAction(() => {
                    console.log("Setting Logged In User", userResult);
                    this.CurrentUser = userResult?.data || {};
                    this.CurrentUserRequest.status = RequestStatus.COMPLETED
                });
            // }
        }
        catch (error) {
            // TODO: Call Log API endpoint
            console.log("Error: " + JSON.stringify(error));
            this.CurrentUserRequest.status = RequestStatus.ERROR
        }
    }

    @action
async setLoggedInUser() {
  try {
    const currentUser = await this.AccountService.currentUserList();

    this.CurrentUserRequest.id = currentUser.data;
    this.CurrentUserRequest.status = RequestStatus.ACTIVE;

    const userResult = await this.UserService.getByUserId(currentUser.data);
    const user = userResult?.data || {};

 runInAction(() => {
  console.log("Setting Logged In User", userResult);

  this.CurrentUserBasic = {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    emailAddress: user.emailAddress,
    username: user.username,
    roles: user.roles,
    profilePic: user.profilePic,
    profilePicId: user.profilePicId,
    state: user.state,
    stateId: user.stateId,
  };

  // Full version stays in memory only
  this.CurrentUser = user;

  this.CurrentUserRequest.status = RequestStatus.COMPLETED;
});
  } catch (error) {
    console.log("Error: " + JSON.stringify(error));
    this.CurrentUserRequest.status = RequestStatus.ERROR;
  }
}

    @action
    async getUsers() {
        try {
            var userResult = await this.UserService.getAllUsers();
            this.UserList = userResult?.data;
        }
        catch (error) {
            console.log("Errror: " + JSON.stringify(error))
        }
    }

    @action
    async getBasicDirectorUser(email: string) {
      try {
        // Make the API request to fetch director data by email
        const response = await this.UserService.getBasicDirectorUserInfoByEmail(email);
    
        // Ensure that response.data is in the expected format and contains the expected properties
        const user: BasicDirectorUserInfoDTO = response.data;
    
        if (user && user.directorUsers && Array.isArray(user.directorUsers) && user.directorUsers.length > 0) {
          const directorId = user.directorUsers[0].id; // Assuming the directorUsers array is always valid
          console.log("Director ID:", directorId);
          return directorId; // Return the directorId for use elsewhere
        } else {
          console.log("No director users found or directorUsers is empty.");
          return null; // Return null if no director is found or the array is empty
        }''
      } catch (error) {
        // Handle any errors that occur during the API request
        console.error("Error fetching the director:", error);
        return null; // Return null in case of error
      }
    }
    

@action
async getBasicDirectorUserByEmail(email: string) {
  try {
    // Make the API request to fetch director data by email
    const response = await this.UserService.getBasicDirectorUserInfoByEmail(email);
    console.log("Director Response from Store", response.data);
    
    // Ensure that response data has the expected structure and contains director users
    if (response.data && response.data.directorUsers && Array.isArray(response.data.directorUsers) && response.data.directorUsers.length > 0) {
      const director = response.data; // Full user details
      console.log("Director Data:", director);
      return director; // Return the full user details, including the director ID and other properties
    } else {
      console.log("No director users found or directorUsers is empty.");
      return null; // Return null if no director is found or the array is empty
    }
  } catch (error) {
    console.error("Error fetching the director:", error);
    return null; // Return null in case of error
  }
}

    
@action
async getBasicUserByEmail(email: string) {
  try {
    // Make the API request to fetch user data by email
    const response = await this.UserService.getBasicUserInfoByEmail(email);
    console.log("User Response from Store", response.data);

    // Return the user data
    return response.data;  // Make sure you're returning the user data here
  } catch (error) {
    console.error("Error fetching user:", error);
    return null;  // Return null in case of an error
  }
}

 

    @action
    async getAllDirectorUsers() {
        try {
            var directorResults = await this.UserService.getAllDirectorUsers()
            if (directorResults?.data) {
                this.AllDirectorUsersList = directorResults.data
            }
        }
        catch (error) {
            console.log("Error: " + JSON.stringify(error));
        }
    }


       async getAllDirectorUsersNew() {
        try {
            var directorResults = await this.DirectorService.getAllDirectorUsersNew()
            if (directorResults?.data) {
                this.AllDirectorUsersList = directorResults.data
            }
        }
        catch (error) {
            console.log("Error: " + JSON.stringify(error));
        }
    }

    // async getAllDirectorUsers(reset:boolean = false) {
    //     try {
    //         if (this?.AllActiveDirectorsListRequest?.status == RequestStatus.ACTIVE || this?.AllActiveDirectorsListRequest?.status == RequestStatus.COMPLETED
    //             && !reset) {
    //             return;
    //         }
    //         this.AllActiveDirectorsListRequest = { id: 0, status: RequestStatus.ACTIVE };
    //         var allActiveDirectorsListResult = await this.UserService.getAllDirectorUsers()
    //         // console.log("data", pageantResult);
    //         if (allActiveDirectorsListResult.data) {
    //             this.AllActiveDirectorsListRequest.status = RequestStatus.COMPLETED;
    //             this.AllDirectorUsersList = allActiveDirectorsListResult?.data;
    //         }
    //     } catch (error) {
    //         this?.AllActiveDirectorsListRequest?.status = RequestStatus.ERROR;
    //         console.log("Error:" + JSON.stringify(error));
    //     }
    // }

    @action
    async getAllCoachUsers() {
        try {
            var coachResults = await this.UserService.getAllCoachUsers()

            if (coachResults?.data) {
                this.AllCoachUsersList = coachResults.data
            }
        }
        catch (error) {
            console.log("Error: " + JSON.stringify(error));
        }
    }

    @action
    async getUser(id): Promise<UserModel | undefined> {
        try {
            if (this.GetUserByIdRequest?.id !== id || this.GetUserByIdRequest?.status !== RequestStatus.ACTIVE) {
                this.GetUserByIdRequest = {
                    id: id,
                    status: RequestStatus.ACTIVE
                };
    
                var userResult = await this.UserService.getByUserId(id);
                this.UserList = [userResult?.data]; // Assuming UserList is an observable array or object in your store
                this.GetUserByIdRequest.status = RequestStatus.COMPLETED;
            }
    
            return this.UserList[0]; // Assuming you only fetch one user
        }
        catch (error) {
            console.log("Error: " + JSON.stringify(error));
            this.GetUserByIdRequest = {
                id: id,
                status: RequestStatus.ERROR
            };
        }
    }

    @action
    async getUserBasicInfo(id): Promise<UserBaseModel | undefined> {
        try {
            // if (this.GetUserByIdRequest?.id !== id || this.GetUserByIdRequest?.status !== RequestStatus.ACTIVE) {
            //     this.GetUserByIdRequest = {
            //         id: id,
            //         status: RequestStatus.ACTIVE
            //     };
    
                var userResult = await this.UserService.getBasicInfoByUserId(id);
                // this.UserList = [userResult?.data]; // Assuming UserList is an observable array or object in your store
                
                // this.GetUserByIdRequest.status = RequestStatus.COMPLETED;
                if (userResult.data) {
                    return userResult.data;    
                }
            // }
            return undefined;
            // return this.UserList[0]; // Assuming you only fetch one user
        }
        catch (error) {
            console.log("Error: " + JSON.stringify(error));
            this.GetUserByIdRequest = {
                id: id,
                status: RequestStatus.ERROR
            };
        }
    }


    

    @action
    async getUser2(id): Promise<UserModel | undefined> {
        try {
            var userResult = await this.UserService.getByUserId(id);
            // this.UserList = [userResult?.data];
            return userResult?.data;
        }
        catch (error) {
            console.log("Errror: " + JSON.stringify(error))
        }
    }

    @action
    async setUserForNav(id) {
        try {
            var user = await this.UserService.getByUserId(id);
            this.SelectedUser = user?.data;
        }
        catch (error) {
            console.log("Error: " + JSON.stringify(error))
        }
    }

    @action
    async resetSelectedUser() {
        this.SelectedUser = undefined;
    }

    @action
    async refreshCurrentUser() {
        if (this.CurrentUser.id && !(this.CurrentUserRequest.id == this.CurrentUser.id && this.CurrentUserRequest.status == RequestStatus.ACTIVE)) {
            this.CurrentUserRequest.id = this.CurrentUser.id
            this.CurrentUserRequest.status = RequestStatus.ACTIVE;
            var result = await this.getUser(this.CurrentUser.id);
            if (result) {
                this.CurrentUser = result;
                this.CurrentUserRequest.status = RequestStatus.COMPLETED;
            } else {
                this.CurrentUserRequest.status = RequestStatus.ERROR;
            }
        }
    }



    @action
    async resetCurrentUser() {
        this.CurrentUser = {};
this.CurrentUserBasic = {};
    }

    @action
    async logout() {
        try {
            var logoutResult = await this.AccountService.logoutCreate();
           this.CurrentUser = {};
this.CurrentUserBasic = {};
            this.UserList = [];
        }
        catch (error) {
            console.log("Error: " + JSON.stringify(error))
        }
    }
}

let userStore: UserStore;

export function GetUserStore() {
    if (!userStore) {
    userStore = new UserStore(
  {},
  {},
  [],
  [],
  [],
  { id: 0, status: RequestStatus.PENDING },
  undefined,
  { id: 0, status: RequestStatus.PENDING }
);
    }
    return userStore;
}